import { IHeaderGroupAngularComp } from '@ag-grid-community/angular';
import { IHeaderGroupParams } from '@ag-grid-community/core';
import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, signal, Type } from '@angular/core';

import { ButtonComponent } from '@shared/components/button/button.component';
import { AuxIconName } from '@shared/components/icon/icon';
import { IconComponent } from '@shared/components/icon/icon.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';

export interface AgColumnGroupHeaderExternalParams {
  buttons?: {
    icon: AuxIconName;
    onClick: () => void;
    tooltip: string;
  }[];
  expansionDisabled?: boolean;
  innerComponent?: Type<unknown>;
  innerComponentParams?: unknown;
  onToggle?:
    | ((expanded: boolean) => void)
    | ((expanded: boolean, params: IHeaderGroupParams) => void);
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, ButtonComponent, TooltipDirective, NgComponentOutlet],
  template: `
    @if (params.innerComponent) {
      <ng-container
        [ngComponentOutlet]="params.innerComponent"
        [ngComponentOutletInputs]="innerComponentParams()"
      ></ng-container>
    } @else {
      <div
        #headerName
        class="truncate"
        [auxTooltip]="headerName.offsetWidth < headerName.scrollWidth ? params.displayName : ''"
      >
        {{ params.displayName }}
      </div>
    }
    @for (btn of buttons(); track $index) {
      <aux-button
        [icon]="btn.icon"
        [auxTooltip]="btn.tooltip"
        (click)="btn.onClick()"
        variant="tertiary"
        classList="ml-1 p-[4px]"
      />
    }

    @if (!params.expansionDisabled) {
      <aux-icon
        [name]="expanded() ? 'ChevronLeft' : 'ChevronRight'"
        class="ml-1 cursor-pointer"
        (click)="setExpanded(!expanded())"
      />
    }
  `,
})
export class AgColumnGroupHeaderComponent implements IHeaderGroupAngularComp {
  buttons = signal<
    {
      icon: AuxIconName;
      onClick: () => void;
      tooltip: string;
    }[]
  >([]);
  expanded = signal(false);

  params!: AgColumnGroupHeaderExternalParams & IHeaderGroupParams;

  innerComponentParams = signal({
    params: this.params,
  });

  @HostBinding('class') classes = 'ag-header-group-cell-label justify-center';

  agInit(params: AgColumnGroupHeaderExternalParams & IHeaderGroupParams): void {
    this.params = params;
    this.innerComponentParams.set({
      params: this.params,
    });

    this.expanded.set(params.columnGroup.isExpanded());

    if (!this.params.expansionDisabled) {
      this.expanded.set(params.columnGroup.isExpanded());
    }

    if (params.buttons) {
      this.buttons.set(params.buttons);
    }
  }

  setExpanded(expanded: boolean): void {
    this.params.setExpanded(expanded);
    this.expanded.set(expanded);

    if (this.params.onToggle) {
      this.params.onToggle(expanded, this.params);
    }
  }
}
